import { format, parseISO } from 'date-fns';
import sub from 'date-fns/sub';
import {
  call,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { buildDataDaiFetchParams } from '../domain/dai/buildDataDaiFetchParams';
import {
  DataDai,
  SettingsOptionsDai,
  UnitDataListFormConditions,
} from '../domain/dai/types';
import { FavoriteItem } from '../domain/favorites';
import { Hall, OrderType } from '../domain/schemas';
import { ShuOption, defaultShu } from '../domain/shu';
import { checkCurrentShuForUnitDataList } from '../domain/unitDataList/checkCurrentShuForUnitDataList';
import { UnitDataListDataViewMode } from '../domain/unitDataList/ranking';
import { resolveUnitDataListFetchHalls } from '../domain/unitDataList/resolveUnitDataListFetchHalls';
import { resolveUnitDataListMainTableEndDate } from '../domain/unitDataList/resolveUnitDataListMainTableEndDate';
import { UnitDataListReportsFavorite } from '../domain/unitDataList/types';

import { dateTypes } from '../components/organisms/UnitDataListTransitionTable/dateTypes';

import {
  DataDaiActionCreators,
  DataDaiActionTypes,
  dataDaiColumnsOrderSelector,
  dataDaiHighlightAverageSelector,
  dataDaiParamsSelector,
} from '../redux/server/dataDai';
import { DataUnitDataListTransitionActionCreators } from '../redux/server/dataUnitDataListTransition';
import {
  settingsOptionsDaiFieldsSelector,
  settingsOptionsDaiSearchConditionSelector,
  settingsOptionsDaiSelector,
} from '../redux/server/settingsOptionsDai';
import { ShortenedUrlActionCreators } from '../redux/server/shortenedUrl';
import { SettingsFavoritesActionCreators } from '../redux/ui/settingsFavorites';
import {
  ChangeUnitDataListReportsCurrentHallsAction,
  ChangeUnitDataListReportsCurrentShuAction,
  ChangeUnitDataListReportsFavoriteAction,
  CreateUnitDataListReportsShortenedUrlAction,
  SaveAsUnitDataListReportsFavoriteAction,
  SaveUnitDataListReportsFavoriteAction,
  SearchResetUnitDataListReportsAction,
  SearchUnitDataListReportsAction,
  SearchUnitDataListReportsDateRangeSlideAction,
  SearchUnitDataListReportsFieldAction,
  UnitDataListReportsSettingActionCreators,
  UnitDataListReportsSettingActionTypes,
  UnitDataListReportsSettingState,
  resetUnitDataListReportsSubMenuNumberFilterAction,
  searchUnitDataListReportsAction,
  selectUnitDataListReportsModelNameFilterAction,
  selectUnitDataListReportsSubMenuNumberFilterAction,
  unitDataListReportsCurrentHallsSelector,
  unitDataListReportsDataViewModeSelector,
  unitDataListReportsKiAlignedSlideLockSelector,
  unitDataListReportsModelNameFilterSelector,
  unitDataListReportsSearchConditionSelector,
  unitDataListReportsSelectedCurrentShuSelector,
  unitDataListReportsSelectedDateRangeParamsSelector,
  unitDataListReportsSelectedFavoriteDataSelector,
  unitDataListReportsSelectedFavoriteSelector,
  unitDataListReportsSelectedFavoriteSettingSelector,
  unitDataListReportsSelectedTableFilterSelector,
  initialState as unitDataListReportsSettingInitialState,
  unitDataListReportsSettingSelector,
  unitDataListReportsSubMenuNumberFilterSelector,
  unitDataListReportsTableHeaderSubMenuMultiFilterSelector,
} from '../redux/ui/unitDataListReportsSetting';
import { userSelector } from '../redux/ui/user';
import { compressToEncodedURIComponent } from '../utils/compressToEncodedURIComponent';
import { customSelect } from '../utils/customSelect';
import { buildColumnsOrder } from '../utils/fieldsOrder';
import { isValidArea } from '../utils/isValidArea';
import {
  KiAlignedYmdSlideLock,
  isKiAlignedSlideDateRange,
  makeKiAlignedSlideDates,
  shouldKeepKiAlignedYmdSlide,
} from '../utils/makeKiAlignedSlideDates';
import { getShuOption } from '../utils/shu';
import {
  UnitDataListReportsDateRange,
  UnitDataListReportsDateUnit,
  makeUnitDataListDateFromDateUnitAndRange,
  makeUnitDataListDateUnit,
  unitDataReportsDateUnits,
} from '../utils/unitDataListDateRange';

/**
 * 選択中の種別の情報を元に期間指定部分の検索条件を加工する
 * @param dateRange 現在選択中の期間
 * @param isComparison 比較期間の有無
 * @param formConditions 現在の検索条件
 * @returns 加工された期間指定の検索条件
 */
const serachContidionToDateRangeParams = (
  dateRange: UnitDataListReportsDateRange,
  dateUnit: UnitDataListReportsDateUnit,
  isComparison: boolean,
  formConditions: UnitDataListFormConditions
): UnitDataListFormConditions => {
  // 期間を元にstartDateとendDateを算出
  let date:
    | ReturnType<typeof makeUnitDataListDateFromDateUnitAndRange>
    | undefined = undefined;

  // 期間指定がカスタムの場合は現在の検索パラメータの値を送る
  if (dateRange !== 'カスタム') {
    // 指定された期間から日付を算出する
    date = makeUnitDataListDateFromDateUnitAndRange(dateRange, dateUnit);
  }

  return {
    // dateが算出されていない場合はカスタムと判断し、保持されている検索パラメータの値をそのまま送る
    // 保持されている検索パラメータにymdListがない場合は、ymdList対応以前のお気に入りと判断し、startDateとendDateからymdListを算出する
    ymdList:
      date !== undefined
        ? date.ymdList.map((date) => format(date, 'yyyy-MM-dd'))
        : formConditions.ymdList !== undefined
        ? formConditions.ymdList
        : undefined,
    // 比較期間の有無で startComparisonDateとendComparisonDate を送るか判定する
    ymdComparisonList: isComparison
      ? date !== undefined
        ? date.ymdComparisonList.map((date) => format(date, 'yyyy-MM-dd'))
        : formConditions.ymdComparisonList !== undefined
        ? formConditions.ymdComparisonList
        : undefined
      : undefined,
  };
};

/**
 * 検索に使う期間を決める。
 * スライドで今週・今月を確定済み、またはその日付を維持する再検索のときは payload の日付を使い、
 * それ以外はテンプレ再計算する。
 */
export const resolveUnitDataListSearchDateParams = (
  shouldUseSlideDates: boolean,
  dateRange: UnitDataListReportsDateRange,
  dateUnit: UnitDataListReportsDateUnit,
  isComparison: boolean,
  formConditions: UnitDataListFormConditions
): Pick<UnitDataListFormConditions, 'ymdList' | 'ymdComparisonList'> => {
  if (shouldUseSlideDates) {
    return {
      ymdList: formConditions.ymdList,
      ymdComparisonList: isComparison
        ? formConditions.ymdComparisonList
        : undefined,
    };
  }

  return serachContidionToDateRangeParams(
    dateRange,
    dateUnit,
    isComparison,
    formConditions
  );
};

/**
 * お気に入りからソート条件を解決する（searchCondition 優先、なければ dataDai.setting を参照）
 */
const resolveFavoriteSortOrder = (
  favorite: UnitDataListReportsFavorite
): { sort: string | undefined; order: OrderType | undefined } => {
  const sort = favorite.searchCondition.sort ?? favorite.dataDai?.setting?.sort;
  const order =
    favorite.searchCondition.order ?? favorite.dataDai?.setting?.order;
  return { sort, order };
};

/**
 * お気に入り選択時、検索条件に反映する
 * @param action Action
 */
function* applyFavoriteByIdSaga(
  action: ChangeUnitDataListReportsFavoriteAction
) {
  // 選択されたお気に入りID
  const selectedFavoriteId = action.payload.favorite;

  // 現在選択中のお気に入りデータを取得
  const favorite: UnitDataListReportsFavorite | undefined = yield select(
    unitDataListReportsSelectedFavoriteSettingSelector
  );

  // 担当店舗取得
  const { hallCodeInCharge }: { hallCodeInCharge: string } = yield select(
    userSelector
  );

  const {
    searchCondition: halls,
  }: {
    searchCondition: {
      halls: Hall[];
    };
  } = yield select(settingsOptionsDaiSelector);

  // settingsOptionと担当店舗から担当店舗の情報を取得
  const findHallCodeInCharge = halls.halls.find(
    (hall) => hall.code === hallCodeInCharge
  );

  // 店舗設定
  const favoriteHalls = () => {
    if (favorite?.searchCondition.halls) {
      return favorite?.searchCondition.halls;
    }
    // 担当店舗が設定されている場合は担当店舗を設定
    else if (hallCodeInCharge && findHallCodeInCharge) {
      return [findHallCodeInCharge.code];
    } else {
      const selectedHall = halls.halls.at(0);
      if (!selectedHall) {
        throw new Error('settingsOptionsに該当する店舗が存在しません');
      }
      return [selectedHall.code];
    }
  };

  // デフォルトのお気に入りを選択した場合、リセットと同じ動作を行う
  if (selectedFavoriteId === undefined) {
    yield put(
      UnitDataListReportsSettingActionCreators.searchResetUnitDataListReportsAction(
        favoriteHalls()
      )
    );
    yield put(
      UnitDataListReportsSettingActionCreators.resetUnitDataListReportsTableFilterAction()
    );
    yield put(
      DataUnitDataListTransitionActionCreators.renewDataUnitDataListTransitionAction()
    );
    // 推移テーブルのsearchParamsをリセット
    yield put(
      UnitDataListReportsSettingActionCreators.resetUnitDataListTransitionSearchParamsAction()
    );

    return;
  }

  if (favorite == null) {
    throw new Error('該当するお気に入りが見つかりません');
  }

  // 台別データ一覧のデータを初期化する
  // お気に入り適用前に既存データをクリアして、次のfetchを明確にする
  yield put(DataDaiActionCreators.renewDataDaiAction());
  yield fork(applyFavoriteSaga, favorite);
}

/**
 * UnitDataListReportsFavoriteの内容を実際に反映させる
 */
export function* applyFavoriteSaga(favorite: UnitDataListReportsFavorite) {
  /** お気に入り適用時、推移テーブルの情報を初期化する */
  yield put(
    DataUnitDataListTransitionActionCreators.renewDataUnitDataListTransitionAction()
  );

  // 表示区分（標準/出玉ランキング）をお気に入りに合わせる
  // （未指定の過去のお気に入りは標準表示とみなす）
  // データ取得はこのsaga内で別途行うため、副作用のないSELECTで状態のみ更新する
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsDataViewModeAction(
      favorite.dataViewMode ?? 'unitData'
    )
  );

  // 機種名フィルタを変更
  yield put(
    selectUnitDataListReportsModelNameFilterAction(favorite.modelNameFilter)
  );

  // サブメニュー内数値フィルタを変更
  // 一度サブメニュー内数値フィルタをクリア
  yield put(resetUnitDataListReportsSubMenuNumberFilterAction());

  yield put(
    selectUnitDataListReportsSubMenuNumberFilterAction(
      favorite.subMenuNumberFilter
    )
  );

  // タイプ列追加前のお気に入りは選択を持たないため、直前の選択を消す
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsTableHeaderSubMenuMultiFilterAction(
      favorite.tableHeaderSubMenuMultiFilter ?? {}
    )
  );

  // 期間を適用する
  const dateRange = favorite.selectedDateRangeParams.dateRange;
  const dateUnit =
    favorite.selectedDateRangeParams.dateUnit ?? unitDataReportsDateUnits[0];
  const isComparison = favorite.selectedDateRangeParams.isComparison;
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsDateRangeParamsAction(
      dateRange,
      dateUnit,
      isComparison,
      { kiAlignedSlideLock: undefined }
    )
  );

  // 検索条件の期間部分を再計算
  const dateRangeParams = serachContidionToDateRangeParams(
    dateRange,
    dateUnit,
    isComparison,
    favorite.searchCondition
  );

  const { sort, order } = resolveFavoriteSortOrder(favorite);

  const favoriteAreas = isValidArea(favorite.searchCondition.areas)
    ? favorite.searchCondition.areas ?? []
    : [];

  const currentHalls = favorite.currentHalls ?? [];

  // 検索条件を適用する
  const searchConditions = {
    ...favorite.searchCondition,
    areas: favoriteAreas,
    // 期間を再算出して上書きする
    ...dateRangeParams,
    sort,
    order,
  };
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsSearchConditionAction(
      searchConditions
    )
  );
  // 表示中の種別を適用する
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsCurrentShuAction(
      favorite.currentShu
    )
  );
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsCurrentHallsAction(
      currentHalls
    )
  );
  // 台別データ一覧テーブルを取得
  if (favorite.dataDai?.setting !== undefined) {
    // 出玉ランキングは機種強調ハイライト・平均合計行を持たない
    const isRanking = (favorite.dataViewMode ?? 'unitData') === 'ranking';

    const fetchParams = {
      ...favorite.dataDai.setting,
      // 期間を再算出して上書きする
      ...dateRangeParams,
      areas: searchConditions.areas,
      halls: resolveUnitDataListFetchHalls(
        currentHalls,
        searchConditions.halls
      ),
      sort,
      order,
    };

    yield put(DataDaiActionCreators.fetchDataDaiAction(fetchParams));

    // 成功/失敗/差し替えのいずれか早いものを待つ。
    // - 失敗時は RENEW_DATA_DAI が dispatch される
    // - FETCH_DATA_DAI は takeLatest のため、後続の表示区分切替や別のお気に入り選択で
    //   自分のフェッチが差し替えられることがある。差し替え時は以降の後処理を行わない
    //   （すでに表示されていない・自分のものではないデータへの後処理を防ぐ）
    const { success } = yield race({
      success: take(DataDaiActionTypes.FETCH_DATA_DAI_SUCCESS),
      failure: take(DataDaiActionTypes.RENEW_DATA_DAI),
      superseded: take(DataDaiActionTypes.FETCH_DATA_DAI),
    });

    if (success) {
      // メインテーブルと同じ検索条件でハイライト平均行を取得する
      // （出玉ランキングはハイライト・平均合計行を持たないため取得しない）
      if (!isRanking && favorite.dataDai.highlightAverage.kiList.length > 0) {
        yield put(
          DataDaiActionCreators.fetchDataDaiHighlightAverageRequestAction(
            fetchParams,
            favorite.dataDai.highlightAverage.kiList,
            favorite.dataDai.highlightAverage.kiName,
            favorite.dataDai.highlightAverage.halls
          )
        );
      }

      if (sort !== undefined && order !== undefined) {
        yield put(
          UnitDataListReportsSettingActionCreators.triggerUnitDataListReportsSortAction(
            sort,
            order
          )
        );
      }

      // テーブル列の並び順を変更
      if (favorite.dataDai.columnsOrder) {
        yield put(
          DataDaiActionCreators.selectDataDaiColumnsOrderAction(
            favorite.dataDai.columnsOrder
          )
        );
      } else {
        // 並び順がない場合はリセットする
        yield put(DataDaiActionCreators.resetDataDaiColumnsOrderAction());
      }

      if (favorite.transitionTable?.searchParams.daiBan.at(0) !== undefined) {
        const selectedDateRange = dateTypes.find(
          (date) => date.id === favorite.transitionTable?.searchParams.dateRange
        );

        // メインテーブルの期間の最終日を取得
        // 呼び出し側が渡した ymdList の最終日を優先する。非カスタムでは保存 endDate を使わない
        const mainTableEndDate = resolveUnitDataListMainTableEndDate({
          dateRange: favorite.selectedDateRangeParams.dateRange,
          ymdList: dateRangeParams.ymdList,
          savedEndDate: favorite.searchCondition.endDate,
          fallbackEndDate: format(new Date(), 'yyyy-MM-dd'),
        });

        const newStartDate = format(
          sub(parseISO(mainTableEndDate), {
            days: selectedDateRange
              ? selectedDateRange.number
              : dateTypes.at(0)?.number,
          }),
          'yyyy-MM-dd'
        );

        /** 台別データ推移テーブルの検索条件を変更 */
        yield put(
          UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionSearchParamsAction(
            {
              ...favorite.transitionTable.searchParams,
              startDate: newStartDate,
              endDate: mainTableEndDate,
            }
          )
        );

        // searchParams の spread には favorite に永続化された isTableAnimationEnabled が
        // 含まれ得る。過去に false で保存された favorite でも適用直後に閉じアニメが誤発火しないよう、
        // 専用アクションで明示的に true へ戻す
        yield put(
          UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionIsTableAnimationEnabledAction(
            true
          )
        );

        /** 台別データ推移テーブルのデータ取得 */
        yield put(
          DataUnitDataListTransitionActionCreators.fetchDataUnitDataListTransitionAction(
            {
              ...favorite.transitionTable.searchParams,
              startDate: newStartDate,
              endDate: mainTableEndDate,
            }
          )
        );

        /**台別データ推移テーブルの並び順を変更 */
        yield put(
          UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionColumnsOrderAction(
            favorite.transitionTable.columnsOrder
          )
        );

        // データ取得完了後にアニメーションを有効にする
        yield put(
          UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionIsTableAnimationEnabledAction(
            true
          )
        );
      }
    }
  }

  // テーブルフィルター項目を取得する
  // 項目一覧を反映する
  yield put(
    UnitDataListReportsSettingActionCreators.applyUnitDataListReportsTableFilterAction(
      favorite.tableFilterItems ?? []
    )
  );
}

/**
 * 検索ボタン押下時に行う処理
 */
function* searchActionSaga(action: SearchUnitDataListReportsAction) {
  // 現在の検索条件項目を取得
  const settingsOptions: SettingsOptionsDai['searchCondition'] = yield select(
    settingsOptionsDaiSearchConditionSelector
  );
  // 現在選択中の種別
  const currentShu: ShuOption | undefined = yield select(
    unitDataListReportsSelectedCurrentShuSelector
  );

  // 推移テーブルのアニメーションを一時的に無効にする
  // 詳細は以下参照
  // https://app.zenhub.com/workspaces/claris-60b56db504e3ff00150ebc22/issues/gh/dkclaris/claris-general/5706
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListTransitionIsTableAnimationEnabledAction(
      false
    )
  );

  // 推移テーブルの情報を一旦リセット
  yield put(
    DataUnitDataListTransitionActionCreators.renewDataUnitDataListTransitionAction()
  );

  // すべての種別・種別グループ一覧
  const allShuOption: ShuOption[] = [
    defaultShu,
    ...getShuOption(settingsOptions),
  ];

  // currentShuが選択可能な種別一覧に含まれていない場合先頭を選択
  const checkedCurrentShu = checkCurrentShuForUnitDataList(
    allShuOption,
    currentShu
  );
  if (checkedCurrentShu === undefined) {
    // 種別一覧が空でフォールバック先がない場合は以降の処理を行わない
    return;
  }

  // currentShuがselectedShu（選択可能な種別一覧）にない場合適切な種別を選択する
  if (
    currentShu !== undefined &&
    currentShu.type !== 'none' &&
    checkedCurrentShu.code !== currentShu.code
  ) {
    // currentShu（選択中の種別）を更新する
    yield put(
      UnitDataListReportsSettingActionCreators.selectUnitDataListReportsCurrentShuAction(
        checkedCurrentShu
      )
    );
  }

  const dateRange = action.payload.dateRange;
  const dateUnit = action.payload.dateUnit;
  const isComparison = action.payload.isComparison;
  const excludeToday = action.payload.params.excludeToday ?? true;
  const kiAlignedSlideLock: KiAlignedYmdSlideLock | undefined = yield select(
    unitDataListReportsKiAlignedSlideLockSelector
  );
  const shouldKeepSlideDates = shouldKeepKiAlignedYmdSlide(
    action.payload.shouldUseSlideDates,
    kiAlignedSlideLock,
    {
      dateRange,
      dateUnit,
      isComparison,
      excludeToday,
      ymdList: action.payload.params.ymdList,
      ymdComparisonList: action.payload.params.ymdComparisonList,
    }
  );

  // 検索条件の期間部分を再計算（スライド確定済みの今週・今月は再計算しない）
  const dateRangeParams = resolveUnitDataListSearchDateParams(
    shouldKeepSlideDates,
    dateRange,
    dateUnit,
    isComparison,
    action.payload.params
  );
  const nextKiAlignedSlideLock: KiAlignedYmdSlideLock | undefined =
    shouldKeepSlideDates &&
    isKiAlignedSlideDateRange(dateRange) &&
    dateRangeParams.ymdList != null
      ? {
          dateRange,
          dateUnit,
          isComparison,
          excludeToday,
          ymdList: dateRangeParams.ymdList,
          ymdComparisonList:
            dateRangeParams.ymdComparisonList ??
            action.payload.params.ymdComparisonList ??
            [],
        }
      : undefined;

  // dateRange・dateUnit（選択中の日付単位・期間）を更新する
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsDateRangeParamsAction(
      dateRange,
      dateUnit,
      isComparison,
      { kiAlignedSlideLock: nextKiAlignedSlideLock }
    )
  );

  // 検索条件の加工
  const modifyParams = {
    ...action.payload.params,
    // 期間を再算出して上書きする
    ...dateRangeParams,
  };

  // searchCondition(選択中の検索条件）を更新する
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsSearchConditionAction(
      modifyParams
    )
  );

  const preserveCurrentHalls = action.payload.preserveCurrentHalls ?? false;
  const currentHalls: string[] = preserveCurrentHalls
    ? yield select(unitDataListReportsCurrentHallsSelector)
    : [];

  if (!preserveCurrentHalls) {
    yield put(
      UnitDataListReportsSettingActionCreators.selectUnitDataListReportsCurrentHallsAction(
        []
      )
    );
  }

  //ハイライトはリセット
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  // データ取得
  yield put(
    DataDaiActionCreators.fetchDataDaiAction(
      buildDataDaiFetchParams(modifyParams, checkedCurrentShu, currentHalls)
    )
  );
}

/**
 * お気に入りに保存するデータの生成
 */
function* setPageSetting() {
  // 現在の台別データ一覧の設定すべて
  const pageSetting: UnitDataListReportsSettingState = yield select(
    unitDataListReportsSettingSelector
  );
  // テーブルの検索条件
  const dataDaiParams: DataDai['setting'] = yield select(dataDaiParamsSelector);

  // テーブル列の並び順
  const dataDaiColumnsOrder: string[] | undefined = yield select(
    dataDaiColumnsOrderSelector
  );
  // 機種名フィルタ
  const modelNameFilter: string = yield select(
    unitDataListReportsModelNameFilterSelector
  );

  // サブメニュー内数値フィルタ
  const subMenuNumberFilter: {
    [field: string]: {
      minimumNumber: number | undefined;
      maximumNumber: number | undefined;
    };
  } = yield select(unitDataListReportsSubMenuNumberFilterSelector);

  const tableHeaderSubMenuMultiFilter = yield* customSelect(
    unitDataListReportsTableHeaderSubMenuMultiFilterSelector
  );

  // テーブルフィルターで選択中の項目
  const tableFilterSelectedItems: string[] | undefined = yield select(
    unitDataListReportsSelectedTableFilterSelector
  );

  // ハイライト情報取得
  const {
    kiList,
    kiName,
    halls,
  }: {
    kiList: string[];
    kiName: string;
    halls: string[];
  } = yield select(dataDaiHighlightAverageSelector);

  // こんなケースはないが念の為
  if (pageSetting.currentShu === undefined) return;

  // 推移テーブルが開いているかどうかは台番（daiBan）の有無で判定
  const isTransitionTableOpen =
    pageSetting.transitionTable.searchParams.daiBan.length > 0;

  // 推移テーブルが閉じている場合（台番が空の場合）はStateの初期値を保存
  const transitionTableData = isTransitionTableOpen
    ? {
        searchParams: {
          ...pageSetting.transitionTable.searchParams,
          // アニメフラグは表示用の一時状態のためお気に入りには常にtrueで保存する。
          // false（検索直後・閉じ中）のまま保存されると、適用時に閉じアニメが誤発火して
          // 復元直後のテーブルが閉じてしまうのを防ぐ
          isTableAnimationEnabled: true,
        },
        columnsOrder: pageSetting.transitionTable.columnsOrder,
      }
    : unitDataListReportsSettingInitialState.transitionTable;

  const currentHalls: string[] = yield select(
    unitDataListReportsCurrentHallsSelector
  );

  const result: FavoriteItem['pageSetting'] = {
    unitDataListReports: {
      dataViewMode: pageSetting.dataViewMode,
      searchCondition: pageSetting.searchCondition,
      currentShu: pageSetting.currentShu,
      currentHalls,
      selectedDateRangeParams: pageSetting.selectedDateRangeParams,
      dataDai: {
        setting: dataDaiParams,
        columnsOrder: dataDaiColumnsOrder ?? [],
        highlightAverage: {
          kiList,
          kiName,
          halls,
        },
      },
      modelNameFilter,
      subMenuNumberFilter,
      tableHeaderSubMenuMultiFilter,
      tableFilterItems: tableFilterSelectedItems,
      transitionTable: transitionTableData,
    },
  };

  return result;
}

/**
 * お気に入り上書き保存時の処理
 */
function* saveFavoriteSaga(action: SaveUnitDataListReportsFavoriteAction) {
  // 選択中のお気に入りID
  const selectedFavoriteId: number | undefined = yield select(
    unitDataListReportsSelectedFavoriteSelector
  );
  // 選択中のお気に入り
  const selectedFavorite: FavoriteItem = yield select(
    unitDataListReportsSelectedFavoriteDataSelector
  );
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  // デフォルトの時は動作しない
  if (selectedFavoriteId === undefined) return;

  let favorite: FavoriteItem = {};
  if (action.payload.mode === 'memo') {
    // メモのみ更新モードでの変更後のお気に入りデータ
    favorite = {
      ...selectedFavorite,
      memo: action.payload.memo,
    };
  } else {
    // 変更後のお気に入りデータ
    favorite = {
      name: action.payload.name,
      isShared: action.payload.isShared,
      pageSetting,
      memo: action.payload.memo,
      privatelySharedUsers: action.payload.sharedUser,
    };
  }

  // お気に入りを上書き
  yield put(
    SettingsFavoritesActionCreators.patchSettingsFavoritesAction(
      selectedFavoriteId,
      favorite,
      { pageName: '台別データ一覧' }
    )
  );
}

/**
 * お気に入り新規保存時の処理
 */
function* saveAsFavoriteSaga(action: SaveAsUnitDataListReportsFavoriteAction) {
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  // 変更後のお気に入りデータ
  const favorite: FavoriteItem = {
    name: action.payload.name,
    isShared: action.payload.isShared,
    pageName: '台別データ一覧',
    pageSetting,
    memo: action.payload.memo,
    privatelySharedUsers: action.payload.sharedUser,
  };

  // お気に入りを新規保存
  yield put(
    SettingsFavoritesActionCreators.postSettingsFavoritesAction([favorite], {})
  );
}

/**
 * 画面共有用短縮URL作成の処理
 */
function* createShortenedUrlSaga(
  action: CreateUnitDataListReportsShortenedUrlAction
) {
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  const compressed = compressToEncodedURIComponent(
    action.payload.pageName,
    pageSetting ?? {}
  );
  const originalUrl = `${action.payload.locationUrl}?q=${compressed}`;

  yield put(ShortenedUrlActionCreators.postShortenedUrlAction({ originalUrl }));
}

/**
 * 検索ボタン押下時、新しい検索条件でデータを取得する
 */
function* handleSearchSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SEARCH_UNIT_DATA_LIST_REPORTS,
    searchActionSaga
  );
}

/**
 * リセットボタン押下時、デフォルトの検索条件または選択中のお気に入りでデータを取得する
 */
function* resetSearchActionSaga(action: SearchResetUnitDataListReportsAction) {
  //ハイライトは一旦リセット
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  // 現在選択中のお気に入りを取得する
  const favoriteId: number | undefined = yield select(
    unitDataListReportsSelectedFavoriteSelector
  );

  // お気に入りが選択されている場合はお気に入り適用
  if (favoriteId !== undefined) {
    yield put(
      UnitDataListReportsSettingActionCreators.changeUnitDataListReportsFavoriteAction(
        favoriteId
      )
    );
    return;
  }

  // デフォルトが選択されている場合は表示区分・検索条件を初期化する
  // （出玉ランキング表示中にリセットした場合、表示区分だけ出玉ランキングのまま
  // 残ると、検索条件は標準の初期値なのに出玉ランキング用のエンドポイントで
  // 再取得される不整合が起きるため、標準表示に戻す）
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsDataViewModeAction(
      'unitData'
    )
  );
  yield put(
    UnitDataListReportsSettingActionCreators.resetUnitDataListReportsSearchConditionAction()
  );
  // 非表示項目をリセットする
  yield put(
    UnitDataListReportsSettingActionCreators.resetUnitDataListReportsTableFilterAction()
  );

  // 推移テーブルの情報を一旦リセット
  yield put(
    DataUnitDataListTransitionActionCreators.renewDataUnitDataListTransitionAction()
  );

  // 推移テーブルのsearchParamsをリセット
  yield put(
    UnitDataListReportsSettingActionCreators.resetUnitDataListTransitionSearchParamsAction()
  );

  // 選択中の期間をリセットする
  yield put(
    UnitDataListReportsSettingActionCreators.resetUnitDataListReportsDateRangeParamsAction()
  );

  let searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );

  // 検索条件に店舗を指定する（初期化された検索条件に店舗情報は無く、未指定の場合、全店舗のデータがレスポンスされる）
  if (action.payload.halls !== undefined) {
    searchCondition = {
      ...searchCondition,
      halls: action.payload.halls,
      areas: [],
    };
    yield put(
      UnitDataListReportsSettingActionCreators.selectUnitDataListReportsSearchConditionAction(
        searchCondition
      )
    );
    yield put(
      UnitDataListReportsSettingActionCreators.selectUnitDataListReportsCurrentHallsAction(
        []
      )
    );
  }
  // 現在の種別を取得
  const currentShu: ShuOption = yield select(
    unitDataListReportsSelectedCurrentShuSelector
  );

  // セルの並び順をリセットしてデフォルト順にする
  // （標準／出玉ランキング双方の退避内容も破棄し、リセット後に他モードへ切り替えた際に
  // 古い並び順が復元されないようにする）
  yield put(DataDaiActionCreators.resetDataDaiColumnsOrderAction());
  yield put(DataDaiActionCreators.resetDataDaiColumnsOrderSnapshotsAction());

  // 機種名フィルタをクリア
  yield put(selectUnitDataListReportsModelNameFilterAction(''));

  // サブメニュー内数値フィルタをクリア
  yield put(resetUnitDataListReportsSubMenuNumberFilterAction());

  // タイプ列の複数選択をクリア
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsTableHeaderSubMenuMultiFilterAction(
      {}
    )
  );

  // データ取得
  yield put(
    DataDaiActionCreators.fetchDataDaiAction(
      buildDataDaiFetchParams(searchCondition, currentShu, [])
    )
  );
}

/**
 * リセットボタン押下時台別データ一覧の全てのデータを破棄する
 */
function* handleResetSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SEARCH_RESET_UNIT_DATA_LIST_REPORTS,
    resetSearchActionSaga
  );
}

/**
 * 変更後の種別でデータを取得する
 */
function* changeCurrentShuSaga(
  action: ChangeUnitDataListReportsCurrentShuAction
) {
  // 種別が選択されていない場合は処理を中断
  if (action.payload.shu === undefined) return;

  // 現在の検索条件を取得
  const searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );

  //ハイライトはリセット
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  // 推移テーブルの台番を空にして閉じる（他の検索条件は保持）
  yield put(
    UnitDataListReportsSettingActionCreators.clearUnitDataListTransitionDaiBanAction()
  );

  const currentHalls: string[] = yield select(
    unitDataListReportsCurrentHallsSelector
  );

  // 変更後の種別でデータを取得する
  yield put(
    DataDaiActionCreators.fetchDataDaiAction(
      buildDataDaiFetchParams(searchCondition, action.payload.shu, currentHalls)
    )
  );
}

/**
 * 選択中の種別変更時、データを取得する
 */
function* handleChangeCurrentShuSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_SHU,
    changeCurrentShuSaga
  );
}

/**
 * 表示区分（標準/出玉ランキング）変更時、現在の検索条件でデータを取得する
 */
function* changeDataViewModeSaga() {
  const currentShu: ShuOption | undefined = yield select(
    unitDataListReportsSelectedCurrentShuSelector
  );
  // 初期ロード前は種別未確定のため何もしない（初期ロード時のfetchに委ねる）
  if (currentShu === undefined) return;

  const searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );
  const currentHalls: string[] = yield select(
    unitDataListReportsCurrentHallsSelector
  );

  // ハイライトはリセット
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  // 推移テーブルの台番を空にして閉じる（他の検索条件は保持）
  yield put(
    UnitDataListReportsSettingActionCreators.clearUnitDataListTransitionDaiBanAction()
  );

  // 列並び順もモードごとに退避・復元する
  // （標準／出玉ランキングは表示項目セットが異なるため列並びを共有せず、
  // 切替のたびに他モードの並び順で上書きされないようにする）
  const nextDataViewMode: UnitDataListDataViewMode = yield select(
    unitDataListReportsDataViewModeSelector
  );
  const outgoingColumnsOrder: string[] | undefined = yield select(
    dataDaiColumnsOrderSelector
  );
  yield put(
    DataDaiActionCreators.snapshotDataDaiColumnsOrderForDataViewModeSwitchAction(
      nextDataViewMode,
      outgoingColumnsOrder ?? []
    )
  );

  // 変更後の表示区分でデータを取得する（エンドポイントは fetchDataDaiSaga 側で振り分ける）
  yield put(
    DataDaiActionCreators.fetchDataDaiAction(
      buildDataDaiFetchParams(searchCondition, currentShu, currentHalls)
    )
  );
}

/**
 * 表示区分変更時、データを取得する
 */
function* handleChangeDataViewModeSaga() {
  // 高速連打時に複数 Worker が最終 state 前提で列並びスナップショット＆ fetch を重ねないよう
  // FETCH_DATA_DAI と同様に takeLatest で最新の切替のみ処理する
  yield takeLatest(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE,
    changeDataViewModeSaga
  );
}

/**
 * 変更後の店舗でデータを取得する
 */
function* changeCurrentHallsSaga(
  action: ChangeUnitDataListReportsCurrentHallsAction
) {
  // 店舗が選択されていない場合は処理を中断
  if (action.payload.halls === undefined) return;

  // 現在の検索条件を取得
  const searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );

  // 現在の種別を取得
  const currentShu: ShuOption = yield select(
    unitDataListReportsSelectedCurrentShuSelector
  );

  //ハイライトはリセット
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  // 推移テーブルの台番を空にして閉じる（他の検索条件は保持）
  yield put(
    UnitDataListReportsSettingActionCreators.clearUnitDataListTransitionDaiBanAction()
  );

  // 変更後の店舗でデータを取得する
  yield put(
    DataDaiActionCreators.fetchDataDaiAction(
      buildDataDaiFetchParams(searchCondition, currentShu, action.payload.halls)
    )
  );
}

/**
 * 選択中の店舗変更時、データを取得する
 */
function* handleChangeCurrentHallsSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS,
    changeCurrentHallsSaga
  );
}

/**
 * テーブル固有の検索条件を変更してデータを取得する
 * @param params 変更後の検索条件
 */
function* searchSearchConditonSaga(params: UnitDataListFormConditions) {
  // 現在の検索条件を取得
  const searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );

  // 新しい検索条件
  const newSearchCondition = {
    ...searchCondition,
    ...params,
  };
  // 現在の種別を取得
  const currentShu: ShuOption = yield select(
    unitDataListReportsSelectedCurrentShuSelector
  );

  // 検索条件を更新
  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsSearchConditionAction(
      newSearchCondition
    )
  );

  const currentHalls: string[] = yield select(
    unitDataListReportsCurrentHallsSelector
  );

  //ハイライトはリセットする
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  // データ取得
  yield put(
    DataDaiActionCreators.fetchDataDaiAction(
      buildDataDaiFetchParams(newSearchCondition, currentShu, currentHalls)
    )
  );
}

/**
 * 表示項目変更時データを取得する
 */
function* searchFieldsSaga(action: SearchUnitDataListReportsFieldAction) {
  const fields = action.payload.fields.map((item) => item.code);
  const fieldsOrder = action.payload.fieldsOrder;

  const currentColumnsOrder = yield* customSelect(dataDaiColumnsOrderSelector);
  const allFields = yield* customSelect(settingsOptionsDaiFieldsSelector);
  if (allFields == null) {
    throw new Error('settingsOptions is not found');
  }

  // 表示区分に応じたマスター表示項目で列順を計算する
  // （標準：dai／出玉ランキング：dtmaRanking。項目セットが異なるため）
  const dataViewMode = yield* customSelect(
    unitDataListReportsDataViewModeSelector
  );
  const masterFields =
    dataViewMode === 'ranking' ? allFields.dtmaRanking ?? [] : allFields.dai;

  const resultOrder = buildColumnsOrder(
    fieldsOrder,
    fields ?? [],
    currentColumnsOrder ?? [],
    masterFields
  );

  yield put(DataDaiActionCreators.selectDataDaiColumnsOrderAction(resultOrder));

  yield fork(searchSearchConditonSaga, { fields });
}

// 期間スライドコンポーネントがクリックされた時に検索期間を設定して再検索を実行する
export function* searchDateRangeSlideSaga(
  action: SearchUnitDataListReportsDateRangeSlideAction
) {
  const { dateRange, ymdList, ymdComparisonList } = action.payload;
  // 検索条件の項目を取得
  const searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );

  const { isComparison, dateUnit } = yield select(
    unitDataListReportsSelectedDateRangeParamsSelector
  );

  const slidDateUnit =
    dateRange === 'カスタム' ? dateUnit : makeUnitDataListDateUnit(dateRange);
  const nextDateUnit = slidDateUnit === 'カスタム' ? dateUnit : slidDateUnit;

  const alignedDates = isKiAlignedSlideDateRange(dateRange)
    ? makeKiAlignedSlideDates(dateRange, {
        excludeToday: searchCondition.excludeToday ?? true,
      })
    : undefined;

  //ハイライトはリセットする
  yield put(DataDaiActionCreators.resetDataDaiHighlightAverageAction());

  yield put(
    searchUnitDataListReportsAction(
      {
        ...searchCondition,
        ymdList: alignedDates?.ymdList ?? ymdList,
        ymdComparisonList: alignedDates?.ymdComparisonList ?? ymdComparisonList,
      },
      dateRange,
      nextDateUnit,
      isComparison,
      {
        preserveCurrentHalls: true,
        shouldUseSlideDates: alignedDates != null,
      }
    )
  );
}

/**
 * テーブル固有の検索条件変更検知時の処理
 */
function* handleTableSearchSaga() {
  // 表示項目変更時
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SEARCH_UNIT_DATA_LIST_REPORTS_FIELDS,
    searchFieldsSaga
  );
}

/**
 * お気に入り関連の処理
 */
function* handleFavoriteSaga() {
  // お気に入り適用
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CHANGE_UNIT_DATA_LIST_REPORTS_FAVORITE,
    applyFavoriteByIdSaga
  );
  // お気に入り上書き保存
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SAVE_UNIT_DATA_LIST_REPORTS_FAVORITE,
    saveFavoriteSaga
  );
  // お気に入り新規保存
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SAVE_AS_UNIT_DATA_LIST_REPORTS_FAVORITE,
    saveAsFavoriteSaga
  );
}

// 期間スライドコンポーネントがクリックされた時に実行する処理
function* handleDateRangeSlideSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.SEARCH_UNIT_DATA_LIST_REPORTS_DATE_RANGE_SLIDE,
    searchDateRangeSlideSaga
  );
}

// 画面共有用短縮URL作成の処理
function* handleShortenedUrlSaga() {
  yield takeEvery(
    UnitDataListReportsSettingActionTypes.CREATE_UNIT_DATA_LIST_REPORTS_SHORTENED_URL,
    createShortenedUrlSaga
  );
}

/**
 * 台別データ一覧の検索フォームに関するタスクを実行する
 */
export function* unitDataListReportsSettingSaga() {
  yield fork(handleSearchSaga);
  yield fork(handleResetSaga);
  yield fork(handleTableSearchSaga);
  yield fork(handleChangeCurrentShuSaga);
  yield fork(handleChangeDataViewModeSaga);
  yield fork(handleChangeCurrentHallsSaga);
  yield fork(handleFavoriteSaga);
  yield fork(handleDateRangeSlideSaga);
  yield fork(handleShortenedUrlSaga);
}
